import LogRocket from "logrocket";

import { getUserEmail } from "../authProvider";
import * as actionTypes from "../constants";
import { UserRole, UserStatus, getUserPrivileges } from "../models";
import { ClientMode } from "../models/system";
import { mapRoleEnumToRole } from "../utils/user";

import type {
  CurrentUserEntity,
  EncoderUserEntity,
  UserPreferencesEntity,
  UserState,
} from "../models";
import type { FacilityPreferences } from "../models/facilitySettings";
import type { EncoderUserFieldChangePayload } from "../scenes/Users/actions/encoderUserFieldChange";

const createEmptyEncoderUser = (): Partial<EncoderUserEntity> => ({
  clientId: "",
  customerId: "",
  firstName: "",
  lastName: "",
  userId: "",
  userName: "",
  email: "",
  isSystemAdmin: false,
  isItAdmin: false,
  status: UserStatus.DISABLED,
  version: "",
  roles: [],
});

export const createEmptyUserState = (token?: string): UserState => ({
  isSystemAdmin: false,
  isItAdmin: false,
  highestRole: UserRole.NOROLE,

  error: false,
  errorDetail: "",
  errorReason: "There was an error validating your credentials.",
  facilities: {},
  id: null,
  isAdviceAdmin: false,
  isPartnerAdviceAdmin: false,
  name: null,
  token: token || "",
  tokenDecoded: null,
  preferences: { HideExternalLinkWarning: false, AnimationDisabled: true },
  loadingPreferences: false,
  clientMode: ClientMode.Standard,
  privileges: getUserPrivileges(),

  account: createEmptyEncoderUser(),
  avatarText: "",
  loading: false,
  loaded: false,
  dirty: false,
  organizationName: "",
  organizationId: "",
});

export const userReducer = (state = createEmptyUserState(), action): UserState => {
  switch (action.type) {
    case actionTypes.INITIALIZE_BEGIN:
      return handleInitializeBegin(state, action.payload);
    case actionTypes.GET_CURRENT_USER_COMPLETED:
      return handleGetCurrentUserCompleted(state, action.payload);
    case actionTypes.GET_CURRENT_USER_ORGANIZATION_COMPLETED:
      return handleGetCurrentUserOrganizationCompleted(state, action.payload);
    case actionTypes.GET_USER_PREFERENCES_BEGIN:
    case actionTypes.SET_USER_PREFERENCE_BEGIN:
      return handleSetUserPreferenceBegin(state);
    case actionTypes.SET_USER_PREFERENCE_COMPLETED:
      return handleSetUserPreferenceCompleted(state, action.payload);
    case actionTypes.SET_USER_PREFERENCE_ERROR:
      return handleSetUserPreferenceError(state, action.payload);
    case actionTypes.GET_USER_PREFERENCES_COMPLETED:
      return handleGetUserPreferencesCompleted(state, action.payload);
    case actionTypes.GET_USER_PREFERENCES_ERROR:
      return handleGetUserPreferencesError(state, action.payload);
    case actionTypes.GET_USER_BEGIN:
      return handleGetEncoderUserBegin(state);
    case actionTypes.GET_USER_COMPLETED:
      return handleGetEncoderUserCompleted(state, action.payload);
    case actionTypes.GET_USER_ERROR:
      return handleGetEncoderUserError(state, action.payload);
    case actionTypes.UPDATE_CURRENT_USER_FIELD:
      return handleUpdateEncoderUserField(state, action.payload);
    case actionTypes.SAVE_CURRENT_USER_BEGIN:
      return handleSaveCurrentUserBegin(state, action.payload);
    case actionTypes.SAVE_CURRENT_USER_COMPLETED:
      return handleSaveCurrentUserCompleted(state, action.payload);
    case actionTypes.SAVE_CURRENT_USER_ERROR:
      return handleSaveEncoderUserError(state, action.payload);
    case actionTypes.SET_FACILITY_PREFERENCES:
      return handleFacilityUpdate(state, action.payload);
    default:
      return state;
  }
};

const handleFacilityUpdate = (
  state: UserState,
  payload: { servicePreferences: FacilityPreferences },
): UserState => {
  const privileges = getUserPrivileges(state, payload.servicePreferences.CurrentUserPermissions);
  return { ...state, privileges };
};

const handleInitializeBegin = (state: UserState, payload: string): UserState => {
  const emptyUser = createEmptyUserState(payload);
  return { ...emptyUser, loading: true };
};

const handleGetCurrentUserCompleted = (state: UserState, payload: CurrentUserEntity): UserState => {
  const userState = {
    ...state,
    allAvailablePermissions: payload.allAvailablePermissions,
    commonPermissionsInAllFacilities: payload.commonPermissionsInAllFacilities,
    highestRole: payload.highestRole,
    isItAdmin: payload.isItAdmin,
    isSystemAdmin: payload.isSystemAdmin,
    facilityRoles: payload.roles,
    loading: false,
    loaded: true,
    clientMode: payload.clientMode,
  } as UserState;

  userState.privileges = getUserPrivileges(userState);
  return userState;
};

const handleGetCurrentUserOrganizationCompleted = (state: UserState, payload): UserState => {
  const userEmail = getUserEmail();
  const userState = {
    ...state,
    account: { ...state.account, email: userEmail },
    organizationName: payload.Name,
    organizationId: payload.ViewId,
  } as UserState;

  LogRocket.identify(userState.account.email, {
    organization: userState.organizationName,
    organizationid: userState.organizationId,
    role: mapRoleEnumToRole(userState.highestRole),
  });
  userState.privileges = getUserPrivileges(userState);
  return userState;
};

const handleSetUserPreferenceBegin = (state: UserState): UserState => ({
  ...state,
  loadingPreferences: true,
});

const handleSetUserPreferenceCompleted = (
  state: UserState,
  payload: UserPreferencesEntity,
): UserState => ({
  ...state,
  preferences: { ...state.preferences, [payload.key]: payload.value },
  loadingPreferences: false,
});

const handleSetUserPreferenceError = (state: UserState, payload: string): UserState => ({
  ...state,
  error: true,
  errorReason: payload,
  errorDetail: "",
  loadingPreferences: false,
});

const handleGetUserPreferencesCompleted = (state: UserState, payload: object): UserState => ({
  ...state,
  preferences: { ...state.preferences, ...payload },
  loadingPreferences: false,
});

const handleGetUserPreferencesError = (state: UserState, payload: string): UserState => ({
  ...state,
  error: true,
  errorReason: payload,
  errorDetail: "",
  loadingPreferences: false,
});

const handleUpdateEncoderUserField = (
  state: UserState,
  payload: EncoderUserFieldChangePayload,
): UserState => {
  const account = { ...state.account, [payload.fieldName]: payload.value, errorText: "" };

  return { ...state, loading: false, account, dirty: true };
};

const handleGetEncoderUserBegin = (state: UserState): UserState => ({ ...state, loading: true });

const handleGetEncoderUserCompleted = (state: UserState, payload: EncoderUserEntity): UserState => {
  const updatedState: UserState = {
    ...state,
    account: payload,
    avatarText: `${payload?.firstName?.slice(0, 1).toUpperCase()}${payload?.lastName?.slice(0, 1).toUpperCase()}`,
    errorDetail: "",
    isSystemAdmin: payload.isSystemAdmin,
    isItAdmin: payload.isItAdmin,
    loading: false,
    loaded: true,
    dirty: false,
  };

  return updatedState;
};

const handleSaveCurrentUserBegin = (state: UserState, payload: EncoderUserEntity): UserState => {
  const updatedState = {
    avatarText: `${payload?.firstName?.slice(0, 1).toUpperCase()}${payload?.lastName?.slice(0, 1).toUpperCase()}`,
    errorText: "",
    loading: true,
    dirty: false,
  };

  return { ...state, ...updatedState };
};

const handleSaveCurrentUserCompleted = (
  state: UserState,
  payload: EncoderUserEntity,
): UserState => {
  const updatedState = { account: payload, errorText: "", loading: false, dirty: false };

  return { ...state, ...updatedState };
};

const handleGetEncoderUserError = (state: UserState, error): UserState => {
  const updatedState = {
    account: createEmptyEncoderUser(),
    errorText: error.message,
    loading: false,
    dirty: false,
  };

  return { ...state, ...updatedState };
};

const handleSaveEncoderUserError = (state: UserState, error): UserState => {
  const updatedState = { errorText: error.message, loading: false, dirty: true };

  return { ...state, ...updatedState };
};
